// content/zones/zone5.ts — THE GILDED VAULT's zone mechanic.
//
//   DESIGN §5 Zone 5: "wealth is a trap — this zone sells you everything,
//   gives you nothing, and punishes the grab you didn't appraise."
//
// Four programs live here, and between them they are the zone:
//
//   1. NO FREE FOOD. ZoneConfig.foodPerRoom is [0,0] (data), so the floor never
//      feeds you. Health is bought at a COFFER or at the chapel altar, or it is
//      not had. That one line of data is what turns gold into life, and every
//      other rule below is a price tag hung off it.
//   2. TOLL DOORS. A cleared room's exit demands 30/60/90 gold by room index
//      (onDoorTouched). Rich, you pay on the way through and hear it land;
//      broke, the door shoves you back into the room you were trying to leave.
//   3. CURSED TREASURE. Every 'T' pile is rolled clean or cursed at room start.
//      Clean glitters white-gold and pays 25-40. Cursed glitters RED — the tell,
//      always, from across the room — and pays 60-90 with an ambush pour or a
//      Marked debuff riding on top of it, plus 8% of the Gloom either way.
//   4. THE SHOP AND THE TRAP. Coffers ('X') sell food and potions mid-room.
//      Chests ('H') pay 40-70 gold, and one in four is a Mimic that BREATHES
//      while it waits. Appraisal is the skill the whole zone is about.
//
// Every literal comes from data/zones/zone5.ts. Numbers written in this file are
// drawing geometry and nothing else.
//
// ---------------------------------------------------------------------------
// FLAGGED IMPLEMENTATION — three places where the frozen seam bent, and how.
//
// (a) COIN MOTES. DESIGN says a dead Coinling leaves gold that "fades in 3s".
//     RunCtx can spawn a pickup but cannot see, move or expire one, so a mote
//     that must vanish on a clock is owned by this file: a fixed pool, drawn by
//     the mechanic, swept up by proximity, expired exactly. The one thing they
//     lose by not being engine pickups is that Snapshot.nav does not list them
//     — deliberate: they are bait, not navigation.
//
// (b) THE MARKED DEBUFF. Nothing in shared code reads PlayerState.markedUntil
//     (grep: it is written and reset by game/player.ts and read by nobody), and
//     enemies carry no zone-facing speed layer. So the mechanic sets the field
//     for anything that later wants it, AND enforces DESIGN's "+15% speed" by
//     hand: on the first frame of the mark every live body banks its speed in a
//     tag and is scaled; on the last, it is restored from the same tag. Bodies
//     that spawn mid-mark are caught on their first frame; bodies that die are
//     reset by configureEnemy on reuse.
//
// (c) FIXTURES ARE DRAWN, NOT SOLID. Coffers and chests stand on plain floor:
//     RunCtx has no set-solid call and game/world.ts is not ours to edit. You
//     can walk through a coffer. The alternative was to bake them into the wall
//     glyphs, which would have made them unreachable furniture instead of shops.
// ---------------------------------------------------------------------------

import type { Draw, Enemy, EnemyDef, Room, RunCtx, Vec, ZoneMechanic } from '../../types'
import {
  ZONE5,
  ZONE5_BOSS_ARENA,
  ZONE5_COLORS,
  ZONE5_ENEMIES,
  ZONE5_GENERATORS,
  ZONE5_PROJECTILES,
  ZONE5_RULES,
} from '../../data/zones/zone5'
import { TUNING } from '../../data/tuning'
import { registerZone, registerZoneContent } from '../../game/zones'
import { moveEnemy, pursue, registerEnemyBrain, roleBrain } from '../../game/enemies'
import { runStats } from '../../game/runstats'
import { TILE } from '../../game/world'
import { emit } from '../../engine/particles'
import { fx } from '../../engine/fx'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'
import type { EnemyEnt } from '../../game/entities'
import type { Run } from '../../game/game'

const R = ZONE5_RULES
const C = ZONE5_COLORS
const TAU = Math.PI * 2
const DEG = Math.PI / 180
const RD = Math.round

// Scratch reused by everything below — nothing in this file allocates per frame.
const dirScratch: Vec = { x: 0, y: 0 }
const sepScratch: Vec = { x: 0, y: 0 }
const posScratch: Vec = { x: 0, y: 0 }
const textAt: Vec = { x: 0, y: 0 }

function num(v: unknown, d: number): number {
  return typeof v === 'number' ? v : d
}

function aiNum(ai: Record<string, unknown>, key: string, d: number): number {
  const v = ai[key]
  return typeof v === 'number' ? v : d
}

/** Shortest signed angle from a to b. */
function angleDelta(a: number, b: number): number {
  let x = b - a
  while (x > Math.PI) x -= TAU
  while (x < -Math.PI) x += TAU
  return x
}

/**
 * The two flag bags the run keeps for zone mechanics, plus the banked meta
 * modifiers. types.ts is frozen and carries none of them, so all three are read
 * structurally — a run without them simply behaves as if nothing is set.
 *   flags.tollbreaker      — shrine row 5: the first toll of each zone is free
 *   flags.tollDiscountMul  — Coin Feather talisman: every toll costs less
 *   mods.altarDiscountMul  — Silver Tongue: "altar and coffer prices -20%"
 *   mods.foodHealAdd       — Hearty Rations: a coffer meal is worth more
 *   mods.goldMagnetRadius  — Coin Feather again: loose coin leans your way
 *   relicFlags.cursedTreasureWeaken — Appraiser's Eye (see rollCursed)
 *   relicFlags.treasureSense        — Diviner's Thread: the curse is marked
 */
interface RunLike {
  flags?: Record<string, number | boolean>
  relicFlags?: Record<string, number>
  mods?: { altarDiscountMul?: number; foodHealAdd?: number; goldMagnetRadius?: number }
}
const NO_FLAGS: Record<string, number | boolean> = {}
const NO_RELICS: Record<string, number> = {}

function flags(ctx: RunCtx): Record<string, number | boolean> {
  return (ctx as unknown as RunLike).flags ?? NO_FLAGS
}
function relicFlags(ctx: RunCtx): Record<string, number> {
  return (ctx as unknown as RunLike).relicFlags ?? NO_RELICS
}
function modNum(ctx: RunCtx, key: 'altarDiscountMul' | 'foodHealAdd' | 'goldMagnetRadius', d: number): number {
  const m = (ctx as unknown as RunLike).mods
  const v = m ? (m as unknown as Record<string, unknown>)[key] : undefined
  return typeof v === 'number' ? v : d
}

// ===========================================================================
// 1. THE FIXTURES — one small table per glyph, rebuilt at room start
// ===========================================================================

interface Pile {
  x: number
  y: number
  cursed: boolean
  taken: boolean
  /** what it paid, kept for the render of the fading afterglow */
  bob: number
}

interface Coffer {
  x: number
  y: number
  uses: number
}

interface Chest {
  x: number
  y: number
  mimic: boolean
  opened: boolean
  /** > 0 while a Mimic is erupting out of it */
  hatchT: number
}

interface Coin {
  x: number
  y: number
  vx: number
  vy: number
  t: number
  value: number
  alive: boolean
}

interface Pour {
  x: number
  y: number
  t: number
  defId: string
  alive: boolean
}

const piles: Pile[] = []
const coffers: Coffer[] = []
const chests: Chest[] = []

/** Fixed pools: the frame loop must never allocate. */
const coins: Coin[] = []
for (let i = 0; i < R.coin.cap; i++) coins.push({ x: 0, y: 0, vx: 0, vy: 0, t: 0, value: 0, alive: false })
let coinHead = 0

const pours: Pour[] = []
for (let i = 0; i < 12; i++) pours.push({ x: 0, y: 0, t: 0, defId: '', alive: false })
let pourHead = 0

// --- room geometry, cached once per room ------------------------------------
let gw = 0
let gh = 0
let floorLayer: HTMLCanvasElement | null = null

function pushCoin(x: number, y: number, vx: number, vy: number, value: number): void {
  const c = coins[coinHead]
  coinHead = (coinHead + 1) % coins.length
  c.x = x
  c.y = y
  c.vx = vx
  c.vy = vy
  c.t = R.coin.seconds
  c.value = value
  c.alive = true
}

function pushPour(x: number, y: number, defId: string, t: number): void {
  const p = pours[pourHead]
  pourHead = (pourHead + 1) % pours.length
  p.x = x
  p.y = y
  p.defId = defId
  p.t = t
  p.alive = true
}

// ===========================================================================
// 2. THE ROSTER'S BRAINS
// ===========================================================================

/** Cheap local separation — game/enemies.ts keeps its own version private. */
function localSep(run: Run, e: EnemyEnt, out: Vec): void {
  out.x = 0
  out.y = 0
  const list = run.enemyPool.live
  const reach = e.radius * 2.4
  run.hash.query(e.pos.x, e.pos.y, reach, (i) => {
    const o = list[i]
    if (o === e || !o.alive) return
    const dx = e.pos.x - o.pos.x
    const dy = e.pos.y - o.pos.y
    const d2 = dx * dx + dy * dy
    const want = e.radius + o.radius
    if (d2 >= want * want) return
    if (d2 < 0.0001) {
      out.x += 0.7
      out.y -= 0.3
      return
    }
    const d = Math.sqrt(d2)
    const push = (want - d) / want
    out.x += (dx / d) * push
    out.y += (dy / d) * push
  })
}

// --- Hollow Plate: a tower shield you have to get around ---------------------
//
// FLAGGED INTERPRETATION — the Shellback device (zone 2), re-cut heavier.
//
// DESIGN §5: "animated armor, frontal tower shield, immune from the front:
// flank, or Maul-stagger; directional vulnerability lever." The shared damage
// path knows exactly one immunity switch, `tags.invulnerable`, and it is
// all-or-nothing — content cannot ask a hit where it came from. So the Plate
// computes the answer itself every step: it turns the shield toward the Warden
// at 1.6 rad/s (slower than the zone-2 Shellback, and comfortably slower than a
// Warden walking a circle) and holds the tag only while the Warden stands inside
// that frontal arc. Step outside it and the tag drops the same frame.
//
// The temporal window sits on top: through the windup, the bash and the 1.35s
// recovery the shield is committed forward and the tag is off outright.
//
// The Maul's stagger is honoured in the mechanic's update() rather than here,
// because game/enemies.ts returns before any custom brain runs while `stunT` is
// live — so a Plate frozen mid-block would otherwise keep blocking. See
// GildedVault.update.

const P_APPROACH = 0
const P_WINDUP = 1
const P_BASH = 2
const P_RECOVER = 3

registerEnemyBrain('z5-plate', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const halfArc = (aiNum(ai, 'frontalArcDeg', 140) * DEG) / 2
  const turnRate = aiNum(ai, 'turnRate', 1.6)
  const lungeRange = aiNum(ai, 'lungeRange', 118)
  const lungeCd = aiNum(ai, 'lungeCooldown', 3.2)
  const windup = aiNum(ai, 'windup', 0.62)
  const lungeSpeed = aiNum(ai, 'lungeSpeed', 430)
  const lungeTime = aiNum(ai, 'lungeTime', 0.3)
  const snapDamage = aiNum(ai, 'snapDamage', 1.7)
  const recover = aiNum(ai, 'recover', 1.35)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const toPlayer = Math.atan2(dy, dx)

  let face = num(e.tags.z5face, toPlayer)
  if (e.state === P_APPROACH || e.state === P_RECOVER) {
    const d = angleDelta(face, toPlayer)
    const step = turnRate * dt * (e.state === P_RECOVER ? 0.3 : 1)
    face += Math.max(-step, Math.min(step, d))
  }
  e.tags.z5face = face
  if (Math.abs(Math.cos(face)) > 0.2) e.facing = Math.cos(face) < 0 ? -1 : 1

  const covered = Math.abs(angleDelta(face, toPlayer)) <= halfArc
  const blocking = e.state === P_APPROACH && covered && e.spawnGrace <= 0
  e.tags.invulnerable = blocking
  e.tags.z5block = blocking

  if (e.state === P_WINDUP) {
    e.windup -= dt
    // sets its feet and raises the shield: no ground covered
    moveEnemy(run, e, -Math.cos(face) * 22, -Math.sin(face) * 22, dt)
    if (e.windup <= 0) {
      e.state = P_BASH
      e.stateT = 0
      e.tags.z5hit = false
      e.kbx = Math.cos(face) * lungeSpeed
      e.kby = Math.sin(face) * lungeSpeed
      run.sfx('swing')
      emit(e.pos.x, e.pos.y, { count: 8, color: C.brass, speed: 130, life: 0.24, size: 3, shape: 1 })
    }
    return
  }

  if (e.state === P_BASH) {
    moveEnemy(run, e, 0, 0, dt)
    if (e.tags.z5hit !== true && dist < e.radius + run.player.radius + 10) {
      e.tags.z5hit = true
      run.damagePlayer(e.touchDamage * snapDamage, e.def.id)
      run.shake(3.5, 0.18)
    }
    if (e.stateT > lungeTime) {
      e.state = P_RECOVER
      e.stateT = 0
      emit(e.pos.x, e.pos.y, { count: 10, color: '#8a7a4a', speed: 90, life: 0.3, size: 3 })
    }
    return
  }

  if (e.state === P_RECOVER) {
    // shield down, plates hanging open: it can barely move and cannot block
    moveEnemy(run, e, -Math.cos(face) * 14, -Math.sin(face) * 14, dt)
    if (e.stateT > recover) {
      e.state = P_APPROACH
      e.stateT = 0
      e.cool = lungeCd
    }
    return
  }

  pursue(run, e, dirScratch)
  localSep(run, e, sepScratch)
  const sp = e.speed * speedMul * (covered ? 1 : 0.8)
  moveEnemy(run, e, (dirScratch.x + sepScratch.x) * sp, (dirScratch.y + sepScratch.y) * sp, dt)
  if (dist < lungeRange + run.player.radius && e.cool <= 0 && e.spawnGrace <= 0) {
    e.state = P_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- Coinling: the stock swarm brain, wrapped so it sounds like money --------
const swarmBrain = roleBrain('swarm')

registerEnemyBrain('z5-coinling', (run, e, dt, speedMul) => {
  swarmBrain(run, e, dt, speedMul)
  // a scatter of coin off the leading edge whenever it commits (state 2 is the
  // shared brain's S_ATTACK): the swarm should sound like a till falling over
  const air = e.state === 2
  if (air !== (e.tags.z5air === true)) {
    e.tags.z5air = air
    if (air) emit(e.pos.x, e.pos.y, { count: 4, color: C.coin, speed: 120, life: 0.22, size: 2, shape: 1 })
  }
})

// --- Reliquary Warden: discs that survive the wall ---------------------------

const W_HOLD = 0
const W_WINDUP = 1
const W_RECOVER = 2

registerEnemyBrain('z5-warden', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const keep = aiNum(ai, 'keepRange', 262)
  const minRange = aiNum(ai, 'minRange', 168)
  const fireRange = aiNum(ai, 'fireRange', 344)
  const cd = aiNum(ai, 'fireCooldown', 3.4)
  const windup = aiNum(ai, 'windup', 0.52)
  const discs = Math.max(1, Math.round(aiNum(ai, 'discs', 2)))
  const fan = aiNum(ai, 'fanDeg', 14) * DEG
  const projId = typeof ai.projectile === 'string' ? (ai.projectile as string) : ''

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  const los = run.world.lineOfSight(e.pos.x, e.pos.y, px, py)
  localSep(run, e, sepScratch)

  if (e.state === W_WINDUP) {
    e.windup -= dt
    moveEnemy(run, e, sepScratch.x * 22, sepScratch.y * 22, dt)
    if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1
    if (e.windup <= 0) {
      e.state = W_RECOVER
      e.stateT = 0
      e.cool = cd
      if (projId) {
        const base = Math.atan2(dy, dx)
        for (let i = 0; i < discs; i++) {
          const a = base + (i - (discs - 1) / 2) * fan
          run.spawnProjectileAt(projId, e.pos.x, e.pos.y - 4, Math.cos(a), Math.sin(a), false)
        }
        run.sfx('shoot')
        emit(e.pos.x, e.pos.y - 4, { count: 8, color: '#ffe9a8', speed: 120, life: 0.28, size: 3, shape: 1 })
      }
    }
    return
  }

  if (e.state === W_RECOVER) {
    moveEnemy(run, e, sepScratch.x * 28, sepScratch.y * 28, dt)
    if (e.stateT > 0.4) {
      e.state = W_HOLD
      e.stateT = 0
    }
    return
  }

  const sp = e.speed * speedMul
  let mx = 0
  let my = 0
  if (dist < minRange) {
    mx = -(dx / dist)
    my = -(dy / dist)
  } else if (dist > keep || !los) {
    pursue(run, e, dirScratch)
    mx = dirScratch.x
    my = dirScratch.y
  } else {
    const s = e.bob * 0.7 + e.pos.x * 0.01
    mx = (-dy / dist) * Math.sin(s)
    my = (dx / dist) * Math.sin(s)
  }
  moveEnemy(run, e, (mx + sepScratch.x * 1.3) * sp, (my + sepScratch.y * 1.3) * sp, dt)
  if (Math.abs(dx) > 6) e.facing = dx < 0 ? -1 : 1

  if (e.cool <= 0 && e.spawnGrace <= 0 && dist < fireRange && los) {
    e.state = W_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// --- Mimic: the thing that was a chest --------------------------------------

const M_STALK = 0
const M_WINDUP = 1
const M_LUNGE = 2
const M_RECOVER = 3

registerEnemyBrain('z5-mimic', (run, e, dt, speedMul) => {
  const ai = e.def.ai as Record<string, unknown>
  const lungeRange = aiNum(ai, 'lungeRange', 150)
  const cd = aiNum(ai, 'lungeCooldown', 2.2)
  const windup = aiNum(ai, 'windup', 0.42)
  const lungeSpeed = aiNum(ai, 'lungeSpeed', 470)
  const lungeTime = aiNum(ai, 'lungeTime', 0.26)
  const biteDamage = aiNum(ai, 'biteDamage', 1.4)
  const biteRange = aiNum(ai, 'biteRange', 46)
  const recover = aiNum(ai, 'recover', 0.6)

  const px = run.player.pos.x
  const py = run.player.pos.y
  const dx = px - e.pos.x
  const dy = py - e.pos.y
  const dist = Math.hypot(dx, dy) || 1
  localSep(run, e, sepScratch)

  if (e.state === M_WINDUP) {
    e.windup -= dt
    // the lid yawns: it rocks back onto its hind corners before it goes
    moveEnemy(run, e, -(dx / dist) * 40, -(dy / dist) * 40, dt)
    e.tags.z5gape = Math.min(1, 1 - e.windup / Math.max(0.01, windup))
    if (e.windup <= 0) {
      e.state = M_LUNGE
      e.stateT = 0
      e.tags.z5hit = false
      e.kbx = (dx / dist) * lungeSpeed
      e.kby = (dy / dist) * lungeSpeed
      run.sfx('swing')
    }
    return
  }

  if (e.state === M_LUNGE) {
    moveEnemy(run, e, 0, 0, dt)
    e.tags.z5gape = 1
    if (e.tags.z5hit !== true && dist < biteRange + run.player.radius) {
      e.tags.z5hit = true
      run.damagePlayer(e.touchDamage * biteDamage, e.def.id)
      run.sfx('hit')
      emit(run.player.pos.x, run.player.pos.y, { count: 10, color: C.cursed, speed: 160, life: 0.25, size: 3 })
    }
    if (e.stateT > lungeTime) {
      e.state = M_RECOVER
      e.stateT = 0
    }
    return
  }

  if (e.state === M_RECOVER) {
    e.tags.z5gape = Math.max(0, num(e.tags.z5gape, 1) - dt * 3)
    moveEnemy(run, e, sepScratch.x * 40, sepScratch.y * 40, dt)
    if (e.stateT > recover) {
      e.state = M_STALK
      e.stateT = 0
      e.cool = cd
    }
    return
  }

  e.tags.z5gape = 0
  pursue(run, e, dirScratch)
  const sp = e.speed * speedMul
  moveEnemy(run, e, (dirScratch.x + sepScratch.x) * sp, (dirScratch.y + sepScratch.y) * sp, dt)
  if (dist < lungeRange && e.cool <= 0 && e.spawnGrace <= 0) {
    e.state = M_WINDUP
    e.stateT = 0
    e.windup = windup
    e.telegraph = windup
    run.sfx('telegraph')
  }
})

// ===========================================================================
// 3. THE MECHANIC
// ===========================================================================

const ROOM_BARKS = [
  'The Gilded Vault. Nothing on this floor is free — read it before you take it.',
  'Plates and wardens. The armour only faces one way, and the discs come back.',
  'Everything at once, and the door at the end wants ninety.',
]

class GildedVault implements ZoneMechanic {
  readonly zoneId = 5 as const

  /** The live run, cached for the render hooks (which only receive a Draw). */
  private ctx: RunCtx | null = null
  private anim = 0
  private inArena = false
  private isRespite = false

  // --- the economy
  /** Gold this zone has taken at coffers (the deed's measure — see data). */
  private spentOnGoods = 0
  /** Gold this zone has taken at doors, for the bark and the report. */
  private spentOnTolls = 0
  private tollPaid = false
  private tollFreeUsed = false
  private tollBarkT = 0
  /** sim time this room's door first refused the Warden (-1 = never). */
  private tollPricedAt = 0
  private frugalTicked = false

  // --- the curse
  private markT = 0
  private markOn = false

  /** TEST SEAM ONLY: force the cursed-grab consequence (zone5Debug.force). */
  private forced: 'ambush' | 'mark' | null = null

  // --- barks, once each per zone
  private saidToll = false
  private saidCursed = false
  private saidChest = false
  private saidMimic = false
  private saidCoffer = false
  private saidMark = false

  // ------------------------------------------------------------------ price

  /** A toll, after the shrine pair and the Coin Feather have had their say. */
  tollPrice(ctx: RunCtx): number {
    const table = R.toll.byRoom
    const i = Math.max(0, Math.min(table.length - 1, ctx.room.index))
    const mul = num(flags(ctx).tollDiscountMul, 1)
    return Math.max(1, Math.round(table[i] * mul))
  }

  /** Coffer goods honour Silver Tongue (shrine row 5: "altar and coffer -20%"). */
  private goodsPrice(ctx: RunCtx, base: number): number {
    return Math.max(1, Math.round(base * modNum(ctx, 'altarDiscountMul', 1)))
  }

  private spend(ctx: RunCtx, amount: number, toll: boolean): void {
    ctx.player.gold = Math.max(0, ctx.player.gold - amount)
    runStats.goldSpent += amount
    if (toll) this.spentOnTolls += amount
    else this.spentOnGoods += amount
  }

  // ------------------------------------------------------------- zone flow

  onZoneStart(ctx: RunCtx): void {
    this.ctx = ctx
    this.spentOnGoods = 0
    this.spentOnTolls = 0
    this.tollFreeUsed = false
    this.frugalTicked = false
    this.saidToll = false
    this.saidCursed = false
    this.saidChest = false
    this.saidMimic = false
    this.saidCoffer = false
    this.saidMark = false
    this.markT = 0
    this.markOn = false
    ctx.bark('The Gilded Vault. It is all yours — at the posted price.')
  }

  onRoomStart(ctx: RunCtx, room: Room): void {
    this.ctx = ctx
    this.anim = 0
    this.inArena = room.template.id === ZONE5_BOSS_ARENA.id
    this.isRespite = room.isRespite
    this.tollPaid = false
    this.tollBarkT = 0
    this.tollPricedAt = 0
    this.markT = 0
    this.setMark(ctx, false)

    piles.length = 0
    coffers.length = 0
    chests.length = 0
    for (const c of coins) c.alive = false
    for (const p of pours) p.alive = false

    gh = room.template.map.length
    gw = room.template.map[0]?.length ?? 0

    if (!this.inArena && !room.isRespite) {
      this.buildTreasure(ctx)
      this.buildCoffers(ctx)
      this.buildChests(ctx)
      this.stockCountingRoom(ctx)
    }
    floorLayer = paintFloorLayer(ctx)

    if (this.inArena || room.isRespite) return
    const line = ROOM_BARKS[Math.min(ROOM_BARKS.length - 1, room.index)]
    if (line) ctx.bark(line)
  }

  onZoneEnd(ctx: RunCtx): void {
    this.setMark(ctx, false)
    this.ctx = null
    piles.length = 0
    coffers.length = 0
    chests.length = 0
    for (const c of coins) c.alive = false
    for (const p of pours) p.alive = false
    floorLayer = null
    gw = 0
    gh = 0
  }

  // ------------------------------------------------------------ room build

  /**
   * Every 'T' tile becomes a pile, rolled clean or cursed off the run's own
   * seeded stream, so a given seed always lays out the same argument. The tell
   * lives in the render, never in a tooltip: cursed piles are red from across
   * the room, and Diviner's Thread (relicFlags.treasureSense) puts a ring under
   * them as well for a player who wants the answer without the eye test.
   */
  private buildTreasure(ctx: RunCtx): void {
    const tiles = ctx.findGlyphTiles('T')
    const T = R.treasure
    for (const t of tiles) {
      const cursed = ctx.rng() < T.cursedChance
      const range = cursed ? T.cursedGold : T.cleanGold
      const value = Math.round(range[0] + ctx.rng() * (range[1] - range[0]))
      piles.push({ x: t.x, y: t.y, cursed, taken: false, bob: ctx.rng() * TAU })
      ctx.spawnPickup({ kind: 'treasure', pos: t, value, tag: cursed ? 'cursed' : 'clean' })
    }
  }

  private buildCoffers(ctx: RunCtx): void {
    const tiles = ctx.findGlyphTiles('X')
    for (const t of tiles) coffers.push({ x: t.x, y: t.y, uses: R.coffer.uses })
  }

  private buildChests(ctx: RunCtx): void {
    const tiles = ctx.findGlyphTiles('H')
    for (const t of tiles) {
      chests.push({ x: t.x, y: t.y, mimic: ctx.rng() < R.chest.mimicChance, opened: false, hatchT: 0 })
    }
  }

  /**
   * THE COUNTING ROOM (DESIGN §5): the zone's timed vault is not a treasure
   * room, it is the *only* place in the Gilded Vault that feeds you. game/rooms
   * .ts stocks every vault with gold, embers and one ration; this adds the rest
   * of the cache, because a zone with no food anywhere owes the player one room
   * where the answer is yes.
   */
  private stockCountingRoom(ctx: RunCtx): void {
    if (R.countingRoomFood <= 0) return
    const tiles = ctx.findGlyphTiles('v')
    if (tiles.length === 0) return
    for (let i = 0; i < R.countingRoomFood; i++) {
      const t = tiles[Math.min(tiles.length - 1, Math.floor(ctx.rng() * tiles.length) + i)]
      if (!t) break
      ctx.spawnPickup({ kind: 'food', pos: t, value: TUNING.player.foodHeal })
    }
  }

  // --------------------------------------------------------------- the toll

  /**
   * TOLL DOORS. The Warden has stepped into a cleared room's exit.
   *
   * FLAGGED CHOICE — pay-and-pass, in one touch. The brief allowed either that
   * or a first-touch that only pays. One touch wins because the alternative
   * teaches the player that walking into an open door sometimes does nothing,
   * which is the exact feeling a game should never produce. The payment is not
   * silent: it lands with a heavy chunk, a bark, coin particles and a shake, so
   * the toll is felt on the way through rather than explained afterwards.
   */
  onDoorTouched(ctx: RunCtx): boolean {
    if (this.inArena || ctx.room.isRespite) return true
    if (this.tollPaid) return true
    const price = this.tollPrice(ctx)

    // Tollbreaker (shrine row 5): the first toll of each zone opens free.
    if (flags(ctx).tollbreaker === true && !this.tollFreeUsed) {
      this.tollFreeUsed = true
      this.tollPaid = true
      ctx.sfx('door')
      ctx.bark('The door counts your coin, finds nothing owed, and opens anyway.')
      fx.floatText('TOLLBREAKER', ctx.player.pos.x, ctx.player.pos.y - 26, C.goldHot, 13)
      return true
    }

    if (ctx.player.gold >= price) {
      this.spend(ctx, price, true)
      this.tollPaid = true
      // the chunk: a low door-bolt under a hard strike, then the coin
      ctx.sfx('door')
      ctx.sfx('hit')
      ctx.shake(4, 0.22)
      ctx.hitstop(0.03)
      fx.floatText(`-${price} GOLD`, ctx.player.pos.x, ctx.player.pos.y - 26, C.gold, 14)
      emit(ctx.player.pos.x, ctx.player.pos.y - 6, {
        count: 16,
        color: C.coin,
        speed: 170,
        speedVar: 110,
        life: 0.45,
        lifeVar: 0.2,
        size: 3,
        grav: 260,
        shape: 1,
      })
      ctx.bark(this.saidToll ? 'The door counts your coin.' : 'The door counts your coin. Every door here does.')
      this.saidToll = true
      return true
    }

    // THE DEBT (see ZONE5_RULES.toll): having been refused once, shoving through
    // a second time empties your purse and puts the shortfall on the Gloom. It
    // is a terrible deal for anyone who could have paid, and it is the reason a
    // broke Warden in a stripped room is never trapped.
    if (this.tollPricedAt > 0 && ctx.time - this.tollPricedAt >= R.toll.debtDelay) {
      const paid = ctx.player.gold
      if (paid > 0) this.spend(ctx, paid, true)
      ctx.gloom.add(R.toll.debtGloom)
      this.tollPaid = true
      ctx.sfx('door')
      ctx.sfx('death')
      ctx.shake(6, 0.4)
      fx.vignette(0.6, '#3a0000')
      fx.floatText(paid > 0 ? `-${paid} GOLD` : 'NOTHING TO TAKE', ctx.player.pos.x, ctx.player.pos.y - 26, C.cursed, 14)
      ctx.bark('The door takes what you have. The deep covers the difference.')
      return true
    }

    // broke: veto, price the door out loud, and shove them back inside so the
    // veto cannot be ground against
    if (this.tollPricedAt <= 0) this.tollPricedAt = ctx.time
    if (this.tollBarkT <= 0) {
      this.tollBarkT = R.toll.barkGap
      ctx.bark(`The door wants ${price}. You have ${ctx.player.gold}. Push through and it takes everything.`)
      ctx.sfx('ui')
      fx.floatText(`${price} GOLD`, ctx.player.pos.x, ctx.player.pos.y - 26, C.cursed, 14)
    }
    const cx = (gw * TILE) / 2
    const cy = (gh * TILE) / 2
    const dx = cx - ctx.player.pos.x
    const dy = cy - ctx.player.pos.y
    const d = Math.hypot(dx, dy) || 1
    ctx.pushPlayer((dx / d) * R.toll.shove, (dy / d) * R.toll.shove)
    return false
  }

  // ------------------------------------------------------- treasure & curse

  /**
   * The intercept. A 'treasure' pickup never resolves through the shared switch
   * — this hook pays it, and decides what it costs.
   */
  onPickupTouched(ctx: RunCtx, p: { kind: string; pos: Vec; value: number; tag?: string }): boolean {
    if (p.kind !== 'treasure') return true
    const cursed = p.tag === 'cursed'
    ctx.addGold(p.value)

    // mark the mechanic's own copy so the heap stops being drawn
    for (let i = 0; i < piles.length; i++) {
      const pile = piles[i]
      if (pile.taken) continue
      if (Math.abs(pile.x - p.pos.x) < 6 && Math.abs(pile.y - p.pos.y) < 6) {
        pile.taken = true
        break
      }
    }

    if (!cursed) {
      // clean treasure SINGS: bright, short, and completely without consequence
      ctx.sfx('pickup')
      ctx.sfx('ui')
      fx.floatText(`+${p.value}`, p.pos.x, p.pos.y - 14, C.clean, 14)
      emit(p.pos.x, p.pos.y, {
        count: 20,
        color: C.goldHot,
        speed: 150,
        speedVar: 90,
        life: 0.5,
        lifeVar: 0.2,
        size: 3,
        grav: 180,
        shape: 1,
      })
      return true
    }

    this.takeCursed(ctx, p.pos.x, p.pos.y, p.value)
    return true
  }

  /**
   * The grab you did not appraise. DESIGN §5 prices it twice: 8% of the Gloom
   * every time ("greed darkens"), plus an ambush pour OR a Marked debuff.
   *
   * FLAGGED READING — Appraiser's Eye. game/relics.ts documents its flag as
   * "cursedTreasureWeaken — cursed-treasure marks apply Marked for N seconds",
   * so a Warden holding it never eats the pour: the curse can only reach them
   * as the shorter mark. That is what "weaken" has to mean for a relic whose
   * flavour is "every curse is a price tag read from the wrong side".
   */
  private takeCursed(ctx: RunCtx, x: number, y: number, value: number): void {
    const T = R.treasure
    ctx.gloom.add(T.cursedGloom)
    // the hum: low, wrong, and nothing like the clean pile's chime
    ctx.sfx('door')
    ctx.sfx('telegraph')
    ctx.shake(3, 0.3)
    fx.floatText(`+${value} CURSED`, x, y - 14, C.cursed, 14)
    fx.vignette(0.5, '#3a0000')
    emit(x, y, {
      count: 26,
      color: C.cursed,
      speed: 190,
      speedVar: 130,
      life: 0.6,
      lifeVar: 0.25,
      size: 4,
      shape: 1,
    })
    if (!this.saidCursed) {
      this.saidCursed = true
      ctx.bark('Red gold. You knew, and you took it anyway.')
    }

    // test steering (zone5Debug.force) outranks everything, so a spec never has
    // to fight the seed for a coin flip. Null in every code path but a spec.
    if (this.forced === 'ambush') {
      this.ambush(ctx, x, y)
      return
    }
    if (this.forced === 'mark') {
      this.mark(ctx, T.mark.seconds)
      return
    }

    const weaken = relicFlags(ctx).cursedTreasureWeaken ?? 0
    if (weaken > 0) {
      this.mark(ctx, weaken)
      return
    }
    if (ctx.rng() < T.ambushChance) this.ambush(ctx, x, y)
    else this.mark(ctx, T.mark.seconds)
  }

  /** The pour: 2-4 bodies land around the Warden behind 0.6s of floor marker. */
  private ambush(ctx: RunCtx, x: number, y: number): void {
    const A = R.treasure.ambush
    const n = A.min + Math.floor(ctx.rng() * (A.max - A.min + 1))
    for (let i = 0; i < n; i++) {
      const defId = A.defs[Math.min(A.defs.length - 1, Math.floor(ctx.rng() * A.defs.length))]
      if (this.findOpenAround(ctx, x, y, A.minRadius, A.maxRadius, posScratch)) {
        pushPour(posScratch.x, posScratch.y, defId, A.telegraph)
      }
    }
    ctx.sfx('generator-die')
    ctx.bark('The hall answers. Something was counting on you taking that.')
  }

  /** A walkable point in the ring around (x,y). Deterministic, bounded tries. */
  private findOpenAround(ctx: RunCtx, x: number, y: number, rMin: number, rMax: number, out: Vec): boolean {
    for (let i = 0; i < 14; i++) {
      const a = ctx.rng() * TAU
      const r = rMin + ctx.rng() * (rMax - rMin)
      const px = x + Math.cos(a) * r
      const py = y + Math.sin(a) * r
      const tx = Math.floor(px / TILE)
      const ty = Math.floor(py / TILE)
      if (tx < 1 || ty < 1 || tx >= gw - 1 || ty >= gh - 1) continue
      if (ctx.isWall(tx, ty)) continue
      out.x = px
      out.y = py
      return true
    }
    return false
  }

  /** MARKED. See the flagged note at the top of this file for the mechanism. */
  private mark(ctx: RunCtx, seconds: number): void {
    this.markT = Math.max(this.markT, seconds)
    ctx.player.markedUntil = ctx.time + this.markT
    this.setMark(ctx, true)
    ctx.sfx('telegraph')
    fx.floatText('MARKED', ctx.player.pos.x, ctx.player.pos.y - 30, C.cursed, 15)
    if (!this.saidMark) {
      this.saidMark = true
      ctx.bark('Marked. They can all smell the coin on you now.')
    }
  }

  /** Apply or lift DESIGN's "+15% speed toward you" across every live body. */
  private setMark(ctx: RunCtx | null, on: boolean): void {
    this.markOn = on
    if (!ctx) return
    const mul = R.treasure.mark.speedMul
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) {
      const e = list[i] as EnemyEnt
      if (!e.alive) continue
      const has = e.tags.z5mark === true
      if (on && !has) {
        e.tags.z5mark = true
        e.tags.z5speed = e.speed
        e.speed *= mul
      } else if (!on && has) {
        e.tags.z5mark = false
        e.speed = num(e.tags.z5speed, e.speed)
      }
    }
  }

  // ----------------------------------------------------------- E: the shops

  /**
   * FLAGGED SIMPLIFICATION — no shop overlay. The altar has a DOM panel because
   * it is a stop-and-read decision with relic cards in it; a coffer is a vending
   * machine in the middle of a fight, and a modal there would freeze the room
   * every time a Warden pressed E next to one. So E buys the thing you need:
   * food while you are hurt, a potion while you are whole, and a bark says what
   * happened and what it cost. The price is on the fixture itself, in the world,
   * before you press anything.
   */
  onInteract(ctx: RunCtx): boolean {
    if (this.inArena || ctx.room.isRespite) return false
    const px = ctx.player.pos.x
    const py = ctx.player.pos.y

    // chests first: if you are standing between both, the trap is the decision
    let best = -1
    let bestD = R.chest.reach * R.chest.reach
    for (let i = 0; i < chests.length; i++) {
      const c = chests[i]
      if (c.opened) continue
      const d = (c.x - px) * (c.x - px) + (c.y - py) * (c.y - py)
      if (d < bestD) {
        bestD = d
        best = i
      }
    }
    if (best >= 0) {
      this.openChest(ctx, chests[best])
      return true
    }

    best = -1
    bestD = R.coffer.reach * R.coffer.reach
    for (let i = 0; i < coffers.length; i++) {
      const c = coffers[i]
      const d = (c.x - px) * (c.x - px) + (c.y - py) * (c.y - py)
      if (d < bestD) {
        bestD = d
        best = i
      }
    }
    if (best >= 0) {
      this.buyFromCoffer(ctx, coffers[best])
      return true
    }
    return false
  }

  private openChest(ctx: RunCtx, c: Chest): void {
    c.opened = true
    if (!c.mimic) {
      const g = R.chest.gold
      const value = Math.round(g[0] + ctx.rng() * (g[1] - g[0]))
      ctx.addGold(value)
      ctx.sfx('pickup')
      ctx.sfx('ui')
      fx.floatText(`+${value}`, c.x, c.y - 18, C.goldHot, 15)
      emit(c.x, c.y - 6, {
        count: 24,
        color: C.coin,
        speed: 190,
        speedVar: 120,
        life: 0.6,
        lifeVar: 0.25,
        size: 3,
        grav: 280,
        shape: 1,
      })
      if (!this.saidChest) {
        this.saidChest = true
        ctx.bark('An honest chest. They are not all honest.')
      }
      return
    }
    // A MIMIC. Half a second of tell it earns by having breathed the whole time.
    c.hatchT = R.chest.telegraph
    ctx.sfx('telegraph')
    ctx.sfx('door')
    ctx.shake(5, 0.35)
    ctx.hitstop(0.05)
    fx.vignette(0.5, '#3a0000')
    emit(c.x, c.y, { count: 20, color: C.cursed, speed: 150, speedVar: 100, life: 0.5, size: 4, shape: 1 })
    if (!this.saidMimic) {
      this.saidMimic = true
      ctx.bark('It was breathing. It was always breathing.')
    } else {
      ctx.bark('Another one that was breathing.')
    }
  }

  private buyFromCoffer(ctx: RunCtx, c: Coffer): void {
    const p = ctx.player
    if (c.uses <= 0) {
      ctx.sfx('ui')
      ctx.bark('The coffer is bare. It sold you what it had.')
      return
    }
    const wantsFood = p.hp < p.maxHp
    if (!wantsFood && p.potions >= p.potionCap) {
      ctx.sfx('ui')
      ctx.bark('Whole, and carrying a potion. Nothing here you can use.')
      return
    }
    const price = this.goodsPrice(ctx, wantsFood ? R.coffer.foodCost : R.coffer.potionCost)
    if (p.gold < price) {
      ctx.sfx('ui')
      ctx.bark(`${wantsFood ? 'A ration' : 'A potion'} is ${price}. You have ${p.gold}.`)
      return
    }
    this.spend(ctx, price, false)
    c.uses--
    if (wantsFood) {
      ctx.healPlayer(TUNING.player.foodHeal + modNum(ctx, 'foodHealAdd', 0))
      ctx.sfx('pickup')
      fx.floatText(`RATION -${price}`, c.x, c.y - 20, C.clean, 13)
    } else {
      p.potions++
      ctx.sfx('potion')
      fx.floatText(`POTION -${price}`, c.x, c.y - 20, '#ff9a5c', 13)
    }
    emit(c.x, c.y - 4, { count: 12, color: C.brass, speed: 110, life: 0.4, size: 3, shape: 1 })
    if (!this.saidCoffer) {
      this.saidCoffer = true
      ctx.bark('The coffer sells. In this zone that is the only way health arrives.')
    }
  }

  // -------------------------------------------------------------- the deaths

  onEnemyDeath(ctx: RunCtx, e: Enemy): void {
    this.ctx = ctx
    if (e.def.id === 'coinling') {
      // The arena grows its own floor gold from these (content/bosses/boss5.ts),
      // so the fading motes belong to the rooms only.
      if (!this.inArena) this.burstCoins(ctx, e.pos.x, e.pos.y)
      return
    }
    if (e.def.id === 'mimic') {
      const g = R.chest.mimicGold
      const value = Math.round(g[0] + ctx.rng() * (g[1] - g[0]))
      ctx.addGold(value)
      ctx.sfx('pickup')
      fx.floatText(`+${value}`, e.pos.x, e.pos.y - 18, C.goldHot, 16)
      emit(e.pos.x, e.pos.y, {
        count: 34,
        color: C.coin,
        speed: 220,
        speedVar: 150,
        life: 0.8,
        lifeVar: 0.3,
        size: 4,
        grav: 300,
        shape: 1,
      })
      ctx.bark('It really was the richest thing in the room.')
    }
  }

  /** DESIGN §5: "die into grabbable gold that fades in 3s — risk-bait loot". */
  private burstCoins(ctx: RunCtx, x: number, y: number): void {
    const K = R.coin
    const n = K.min + Math.floor(ctx.rng() * (K.max - K.min + 1))
    for (let i = 0; i < n; i++) {
      const a = ctx.rng() * TAU
      const r = K.spread * (0.35 + ctx.rng() * 0.65)
      pushCoin(x, y, Math.cos(a) * r * 2.4, Math.sin(a) * r * 2.4, K.value)
    }
    ctx.sfx('pickup')
    emit(x, y, { count: 10, color: C.coin, speed: 160, life: 0.35, size: 3, grav: 260, shape: 1 })
  }

  // ---------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.ctx = ctx
    this.anim += dt
    if (this.tollBarkT > 0) this.tollBarkT -= dt

    // A staggered Plate cannot keep blocking: game/enemies.ts returns before any
    // custom brain while tags.stunT is live, so the Maul's answer to a shield is
    // applied from here, where it is guaranteed to run every step.
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) {
      const e = list[i]
      if (!e.alive) continue
      if (e.def.id === 'hollow_plate' && num(e.tags.stunT, 0) > 0) {
        e.tags.invulnerable = false
        e.tags.z5block = false
      }
    }

    // the mark. Re-asserted every frame rather than applied once, because
    // anything that spawns mid-mark (an ambush pour, a generator's next pour)
    // has to arrive already carrying it.
    if (this.markT > 0) {
      this.markT -= dt
      this.setMark(ctx, true)
      if (this.markT <= 0) {
        this.markT = 0
        this.setMark(ctx, false)
        ctx.player.markedUntil = 0
        ctx.bark('The mark fades. Whatever it told them, they heard.')
      }
    }

    this.updateCoins(ctx, dt)
    this.updatePours(ctx, dt)
    this.updateChests(ctx, dt)

    // THE DEED. The arena is only "cleared" once the Hoard is down and the stair
    // is cut (game/game.ts openDescent), so this is the exact moment the zone is
    // beaten — and the last moment the mechanic is still alive to score it.
    if (this.inArena && ctx.room.cleared && !this.frugalTicked) {
      this.frugalTicked = true
      const spent = this.frugalSpend()
      if (spent < R.frugal.limit) {
        ctx.featTick('z5Frugal')
        ctx.bark(`Appraiser. You bought ${spent} gold of help and needed none of it.`)
        fx.floatText('APPRAISER', ctx.player.pos.x, ctx.player.pos.y - 40, C.goldHot, 16)
      }
    }
  }

  /** What the deed measures. See ZONE5_RULES.frugal for why tolls are optional. */
  private frugalSpend(): number {
    return this.spentOnGoods + (R.frugal.countTolls ? this.spentOnTolls : 0)
  }

  private updateCoins(ctx: RunCtx, dt: number): void {
    const K = R.coin
    const px = ctx.player.pos.x
    const py = ctx.player.pos.y
    const magnet = modNum(ctx, 'goldMagnetRadius', 0)
    // PlayerState (frozen) does not carry the body radius; the tuning table does.
    const reach = K.reach + TUNING.player.radius
    const drag = Math.pow(0.02, dt)
    for (let i = 0; i < coins.length; i++) {
      const c = coins[i]
      if (!c.alive) continue
      c.t -= dt
      if (c.t <= 0) {
        c.alive = false
        emit(c.x, c.y, { count: 3, color: C.goldDeep, speed: 30, life: 0.35, size: 2, shape: 1 })
        continue
      }
      c.x += c.vx * dt
      c.y += c.vy * dt
      c.vx *= drag
      c.vy *= drag
      const dx = px - c.x
      const dy = py - c.y
      const d = Math.hypot(dx, dy) || 1
      // Coin Feather pulls loose coin the way it pulls engine gold
      if (magnet > 0 && d < magnet) {
        const pull = (200 * (1 - d / magnet) + 60) * dt
        c.x += (dx / d) * pull
        c.y += (dy / d) * pull
      }
      if (d <= reach) {
        c.alive = false
        ctx.addGold(c.value)
        ctx.sfx('pickup')
        fx.floatText(`+${c.value}`, c.x, c.y - 8, C.coin, 10)
      }
    }
  }

  private updatePours(ctx: RunCtx, dt: number): void {
    for (let i = 0; i < pours.length; i++) {
      const p = pours[i]
      if (!p.alive) continue
      p.t -= dt
      if (p.t > 0) continue
      p.alive = false
      posScratch.x = p.x
      posScratch.y = p.y
      ctx.spawnEnemy(p.defId, posScratch)
      ctx.sfx('door')
      emit(p.x, p.y, {
        count: 18,
        color: C.cursed,
        speed: 170,
        speedVar: 110,
        life: 0.5,
        lifeVar: 0.2,
        size: 4,
        shape: 1,
      })
    }
  }

  private updateChests(ctx: RunCtx, dt: number): void {
    for (let i = 0; i < chests.length; i++) {
      const c = chests[i]
      if (c.hatchT <= 0) continue
      c.hatchT -= dt
      if (c.hatchT > 0) continue
      c.hatchT = 0
      posScratch.x = c.x
      posScratch.y = c.y
      const m = ctx.spawnEnemy('mimic', posScratch, { elite: null })
      if (m.alive) m.tags.z5gape = 1
      ctx.sfx('generator-die')
      ctx.shake(6, 0.4)
      emit(c.x, c.y, {
        count: 30,
        color: '#5c3a1c',
        speed: 220,
        speedVar: 140,
        life: 0.6,
        lifeVar: 0.25,
        size: 4,
        grav: 260,
      })
    }
  }

  // ---------------------------------------------------------------- render

  /** Beneath the bodies: gilded floor, treasure heaps, coin motes, pour marks. */
  renderUnder(d: Draw): void {
    const run = this.ctx
    if (!run || gw === 0) return
    const ctx = d.ctx
    const t = this.anim

    if (floorLayer) {
      ctx.drawImage(floorLayer, 0, 0)
    }

    // --- treasure heaps. THE TELL: cursed piles sit in a red pool and throw red
    // motes; clean piles sit in white-gold. It has to be answerable at a glance
    // across a whole hall, so the difference is the colour of the LIGHT, not a
    // detail on the sprite.
    const sense = (relicFlags(run).treasureSense ?? 0) > 0
    for (let i = 0; i < piles.length; i++) {
      const p = piles[i]
      if (p.taken) continue
      const pulse = 0.55 + 0.45 * Math.sin(t * (p.cursed ? 7 : 3) + p.bob)
      // a dark pool first: on a gold floor, the thing that makes loot pop is the
      // shadow under it, not the shine on it
      ctx.globalAlpha = 0.5
      ctx.fillStyle = C.shadow
      ctx.beginPath()
      ctx.ellipse(p.x, p.y + 8, 26, 11, 0, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 0.2 + 0.26 * pulse
      ctx.fillStyle = p.cursed ? C.cursed : C.goldHot
      ctx.beginPath()
      ctx.ellipse(p.x, p.y + 6, 32 + pulse * 8, 16 + pulse * 4, 0, 0, TAU)
      ctx.fill()
      // and a rim on the pool, so "there is treasure here" carries across a hall
      ctx.globalAlpha = 0.4 + 0.35 * pulse
      ctx.strokeStyle = p.cursed ? C.cursed : C.clean
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.ellipse(p.x, p.y + 6, 30, 14, 0, 0, TAU)
      ctx.stroke()
      ctx.globalAlpha = 1
      drawSprite(ctx, p.cursed ? 'z5_pile_cursed' : 'z5_pile_clean', p.x, p.y)
      if (p.cursed && sense) {
        // Diviner's Thread: a ring, so the answer does not need the eye test
        ctx.globalAlpha = 0.75
        ctx.strokeStyle = C.cursed
        ctx.lineWidth = 2
        ctx.setLineDash([5, 5])
        ctx.beginPath()
        ctx.arc(p.x, p.y + 4, 26, 0, TAU)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.globalAlpha = 1
      }
    }

    // --- coin motes, on the floor where they fell, shrinking as they go
    for (let i = 0; i < coins.length; i++) {
      const c = coins[i]
      if (!c.alive) continue
      const k = Math.max(0, Math.min(1, c.t / R.coin.seconds))
      const r = 3 + k * 3
      ctx.globalAlpha = 0.25 * k
      ctx.fillStyle = C.coin
      ctx.beginPath()
      ctx.arc(c.x, c.y, r * 3.2, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 0.35 + 0.65 * k
      ctx.fillStyle = k > 0.35 ? C.coin : C.goldDeep
      ctx.beginPath()
      ctx.ellipse(c.x, c.y, r, r * 0.7, 0, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 0.9 * k
      ctx.fillStyle = C.goldHot
      ctx.fillRect(RD(c.x) - 1, RD(c.y) - 2, 2, 2)
      ctx.globalAlpha = 1
    }

    // --- ambush markers: the floor tells you where, 0.6s before it happens
    for (let i = 0; i < pours.length; i++) {
      const p = pours[i]
      if (!p.alive) continue
      const k = 1 - Math.max(0, p.t / R.treasure.ambush.telegraph)
      ctx.globalAlpha = 0.25 + 0.45 * k
      ctx.strokeStyle = C.cursed
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.arc(p.x, p.y, 30 - k * 12, 0, TAU)
      ctx.stroke()
      ctx.globalAlpha = 0.12 + 0.3 * k
      ctx.fillStyle = C.cursedDeep
      ctx.beginPath()
      ctx.arc(p.x, p.y, 30 - k * 12, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 1
    }

    // --- fixtures. Drawn under the bodies so a Plate standing in front of a
    // coffer reads as standing in front of it.
    for (let i = 0; i < coffers.length; i++) {
      const c = coffers[i]
      drawSprite(ctx, c.uses > 0 ? 'z5_coffer' : 'z5_coffer_spent', c.x, c.y - 4)
    }
    for (let i = 0; i < chests.length; i++) {
      const c = chests[i]
      if (c.opened && c.hatchT <= 0) {
        if (!c.mimic) drawSprite(ctx, 'z5_chest_open', c.x, c.y - 2)
        continue
      }
      // THE TELL: a Mimic breathes. Always, from the moment the room is built,
      // whether or not anyone is looking at it.
      const breathe = c.mimic ? 1 + 0.055 * Math.sin((t * TAU) / R.chest.breathPeriod + c.x * 0.01) : 1
      const lift = c.mimic ? Math.sin((t * TAU) / R.chest.breathPeriod + c.x * 0.01) * 1.5 : 0
      if (c.hatchT > 0) {
        const k = 1 - c.hatchT / R.chest.telegraph
        ctx.globalAlpha = 0.35 + 0.5 * k
        ctx.fillStyle = C.cursed
        ctx.beginPath()
        ctx.arc(c.x, c.y, 26 + k * 16, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 1
      }
      drawSprite(ctx, 'z5_chest', c.x, c.y - 2 + lift, { scale: breathe })
    }
  }

  /** Over the bodies: the shield arcs, the price tags, and the mark. */
  renderOver(d: Draw): void {
    const run = this.ctx
    if (!run) return
    const ctx = d.ctx

    // --- the Hollow Plate's shield, exactly as wide as the immunity
    const list = run.enemies
    ctx.save()
    for (let i = 0; i < list.length; i++) {
      const e = list[i]
      if (!e.alive || e.def.id !== 'hollow_plate') continue
      const face = num(e.tags.z5face, 0)
      const half = (aiNum(e.def.ai as Record<string, unknown>, 'frontalArcDeg', 140) * DEG) / 2
      const blocking = e.tags.z5block === true
      const r = 17 + (e.elite === 'bloated' ? 5 : 0)
      if (blocking) {
        ctx.globalAlpha = 0.9
        ctx.strokeStyle = C.plate
        ctx.lineWidth = 6
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, r, face - half, face + half)
        ctx.stroke()
        ctx.globalAlpha = 0.28
        ctx.strokeStyle = C.goldHot
        ctx.lineWidth = 11
        ctx.stroke()
      } else {
        ctx.globalAlpha = 0.8
        ctx.strokeStyle = '#ff8a6a'
        ctx.lineWidth = 3
        ctx.setLineDash([5, 5])
        ctx.beginPath()
        ctx.arc(e.pos.x, e.pos.y, r, face - half, face + half)
        ctx.stroke()
        ctx.setLineDash([])
      }
    }
    ctx.restore()

    if (this.inArena || this.isRespite) return

    // --- price tags. A shop you have to guess at is not a shop.
    const px = run.player.pos.x
    const py = run.player.pos.y
    for (let i = 0; i < coffers.length; i++) {
      const c = coffers[i]
      const d2 = (c.x - px) * (c.x - px) + (c.y - py) * (c.y - py)
      if (d2 > 300 * 300) continue
      const inReach = d2 <= R.coffer.reach * R.coffer.reach
      textAt.x = c.x
      textAt.y = c.y - 30
      if (c.uses <= 0) {
        d.text('BARE', textAt, 10, 'rgba(232,220,191,0.45)')
      } else if (inReach) {
        const hurt = run.player.hp < run.player.maxHp
        const price = this.goodsPrice(run, hurt ? R.coffer.foodCost : R.coffer.potionCost)
        d.text(`E — ${hurt ? 'RATION' : 'POTION'} ${price}g`, textAt, 12, C.goldHot)
      } else {
        d.text(`COFFER  ${this.goodsPrice(run, R.coffer.foodCost)}/${this.goodsPrice(run, R.coffer.potionCost)}g`, textAt, 10, 'rgba(232,220,191,0.55)')
      }
    }
    for (let i = 0; i < chests.length; i++) {
      const c = chests[i]
      if (c.opened) continue
      const d2 = (c.x - px) * (c.x - px) + (c.y - py) * (c.y - py)
      if (d2 > R.chest.reach * R.chest.reach) continue
      textAt.x = c.x
      textAt.y = c.y - 30
      d.text('E — OPEN', textAt, 12, C.goldHot)
    }

    // --- the mark, worn where you cannot miss it
    if (this.markT > 0) {
      const k = 0.5 + 0.5 * Math.sin(this.anim * 12)
      ctx.save()
      ctx.globalAlpha = 0.35 + 0.4 * k
      ctx.strokeStyle = C.cursed
      ctx.lineWidth = 2.5
      ctx.beginPath()
      ctx.arc(px, py, 24 + k * 5, 0, TAU)
      ctx.stroke()
      for (let i = 0; i < 4; i++) {
        const a = this.anim * 1.6 + (i / 4) * TAU
        const x0 = px + Math.cos(a) * 28
        const y0 = py + Math.sin(a) * 28
        ctx.beginPath()
        ctx.moveTo(x0, y0)
        ctx.lineTo(px + Math.cos(a) * 38, py + Math.sin(a) * 38)
        ctx.stroke()
      }
      ctx.restore()
      textAt.x = px
      textAt.y = py - 46
      d.text(`MARKED ${this.markT.toFixed(1)}`, textAt, 12, C.cursed)
    }
  }

  // --------------------------------------------------------------- test seam
  /** Read/steer the zone's economy. Used only by test/zone5.mjs. */
  debugState(): {
    inArena: boolean
    isRespite: boolean
    tollPaid: boolean
    tollPrice: number
    tollFreeUsed: boolean
    spentOnGoods: number
    spentOnTolls: number
    frugalSpend: number
    frugalTicked: boolean
    marked: number
    piles: { x: number; y: number; cursed: boolean; taken: boolean }[]
    coffers: { x: number; y: number; uses: number }[]
    chests: { x: number; y: number; mimic: boolean; opened: boolean; hatchT: number }[]
    coins: number
    pours: number
  } {
    const run = this.ctx
    return {
      inArena: this.inArena,
      isRespite: this.isRespite,
      tollPaid: this.tollPaid,
      tollPrice: run ? this.tollPrice(run) : 0,
      tollFreeUsed: this.tollFreeUsed,
      spentOnGoods: this.spentOnGoods,
      spentOnTolls: this.spentOnTolls,
      frugalSpend: this.frugalSpend(),
      frugalTicked: this.frugalTicked,
      marked: this.markT,
      piles: piles.map((p) => ({ x: p.x, y: p.y, cursed: p.cursed, taken: p.taken })),
      coffers: coffers.map((c) => ({ x: c.x, y: c.y, uses: c.uses })),
      chests: chests.map((c) => ({ x: c.x, y: c.y, mimic: c.mimic, opened: c.opened, hatchT: c.hatchT })),
      coins: coins.reduce((n, c) => n + (c.alive ? 1 : 0), 0),
      pours: pours.reduce((n, p) => n + (p.alive ? 1 : 0), 0),
    }
  }

  /** Force the next chest at index i to be (or not be) a Mimic. */
  debugSetMimic(i: number, mimic: boolean): void {
    const c = chests[i]
    if (c) c.mimic = mimic
  }

  /** Force pile i's tag, so a spec never has to fight the seed. */
  debugSetCursed(ctx: RunCtx, i: number, cursed: boolean): void {
    const p = piles[i]
    if (!p || p.taken) return
    p.cursed = cursed
    const T = R.treasure
    const range = cursed ? T.cursedGold : T.cleanGold
    const value = Math.round((range[0] + range[1]) / 2)
    ctx.removePickupsAt({ x: p.x, y: p.y }, 8, 'treasure')
    ctx.spawnPickup({ kind: 'treasure', pos: { x: p.x, y: p.y }, value, tag: cursed ? 'cursed' : 'clean' })
  }

  debugMark(ctx: RunCtx, seconds: number): void {
    this.mark(ctx, seconds)
  }

  /** Force (or release) the cursed-grab consequence. */
  debugForce(kind: 'ambush' | 'mark' | null): void {
    this.forced = kind
  }

  /** Lift the mark now (a fresh mark only ever extends, never shortens). */
  debugClearMark(ctx: RunCtx): void {
    this.markT = 0
    this.setMark(ctx, false)
    ctx.player.markedUntil = 0
  }

  /** Restock every coffer in the room. */
  debugRefillCoffers(): void {
    for (const c of coffers) c.uses = R.coffer.uses
  }

  /** Un-pay this room's toll, so one page can probe several door states. */
  debugResetToll(freeUsed = false): void {
    this.tollPaid = false
    this.tollFreeUsed = freeUsed
    this.tollBarkT = 0
    this.tollPricedAt = 0
  }

  /** Zero the zone's ledger (used to prove the deed measures what it claims). */
  debugResetSpend(): void {
    this.spentOnGoods = 0
    this.spentOnTolls = 0
    this.frugalTicked = false
  }
}

export const zone5Mechanic = new GildedVault()

/**
 * TEST SEAM (test/zone5.mjs). Reading the zone's ledger and forcing a pile's tag
 * is the only way to assert the economy without fighting the seed for an hour.
 * Inert unless something calls it; nothing in the game does.
 */
export const zone5Debug = {
  state: (): ReturnType<GildedVault['debugState']> => zone5Mechanic.debugState(),
  setMimic: (i: number, m: boolean): void => zone5Mechanic.debugSetMimic(i, m),
  setCursed: (ctx: RunCtx, i: number, c: boolean): void => zone5Mechanic.debugSetCursed(ctx, i, c),
  mark: (ctx: RunCtx, s: number): void => zone5Mechanic.debugMark(ctx, s),
  force: (kind: 'ambush' | 'mark' | null): void => zone5Mechanic.debugForce(kind),
  clearMark: (ctx: RunCtx): void => zone5Mechanic.debugClearMark(ctx),
  refillCoffers: (): void => zone5Mechanic.debugRefillCoffers(),
  resetToll: (freeUsed?: boolean): void => zone5Mechanic.debugResetToll(freeUsed),
  resetSpend: (): void => zone5Mechanic.debugResetSpend(),
}

// ===========================================================================
// 4. THE FLOOR LAYER
// One canvas per room: gilded inlay, coin drifts banked against the walls, and
// the tarnish that says this money has been sitting here a long time.
// Composited once at room build; the frame loop blits it.
// ===========================================================================

function paintFloorLayer(ctx: RunCtx): HTMLCanvasElement | null {
  if (gw === 0 || gh === 0) return null
  const c = document.createElement('canvas')
  c.width = gw * TILE
  c.height = gh * TILE
  const g = c.getContext('2d')
  if (!g) return null
  g.imageSmoothingEnabled = false

  for (let ty = 1; ty < gh - 1; ty++) {
    for (let tx = 1; tx < gw - 1; tx++) {
      if (ctx.isWall(tx, ty)) continue
      const x = tx * TILE
      const y = ty * TILE
      const h = (tx * 73856093) ^ (ty * 19349663)
      const r = (h >>> 8) & 255

      // gilded inlay: a diamond every few tiles, tarnished at the edges
      if (((tx + ty) & 3) === 0) {
        g.globalAlpha = 0.13
        g.fillStyle = C.brass
        g.beginPath()
        g.moveTo(x + 16, y + 5)
        g.lineTo(x + 27, y + 16)
        g.lineTo(x + 16, y + 27)
        g.lineTo(x + 5, y + 16)
        g.closePath()
        g.fill()
        g.globalAlpha = 0.1
        g.fillStyle = C.shadow
        g.beginPath()
        g.moveTo(x + 16, y + 9)
        g.lineTo(x + 23, y + 16)
        g.lineTo(x + 16, y + 23)
        g.lineTo(x + 9, y + 16)
        g.closePath()
        g.fill()
      }

      // coin drifts bank up against the walls: the treasury is not tidy
      const nearWall =
        ctx.isWall(tx - 1, ty) || ctx.isWall(tx + 1, ty) || ctx.isWall(tx, ty - 1) || ctx.isWall(tx, ty + 1)
      if (nearWall && r > 90) {
        g.globalAlpha = 0.5
        for (let i = 0; i < 5; i++) {
          const px = x + 3 + ((r * (i + 3)) % 26)
          const py = y + 3 + ((r * (i + 7)) % 26)
          g.fillStyle = i % 2 === 0 ? C.gold : C.goldDeep
          g.fillRect(px, py, 3, 2)
        }
      } else if (r > 232) {
        g.globalAlpha = 0.35
        g.fillStyle = C.goldDeep
        g.fillRect(x + (r % 20) + 4, y + ((r >> 3) % 20) + 4, 3, 2)
      }
    }
  }
  g.globalAlpha = 1
  return c
}

// ===========================================================================
// 5. SPRITES
// Chunky, dark-outlined, and gold-first. Nothing here shares a silhouette with
// an earlier zone: the Gilded Vault is boxes, plates and coins — hard edges and
// straight lines, where the Pale Court was tall and narrow and the Drowned
// Roots was round.
// ===========================================================================

function pstr(p: Record<string, number | string>, k: string, d: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : d
}

function pnum(p: Record<string, number | string>, k: string, d: number): number {
  const v = p[k]
  return typeof v === 'number' ? v : d
}

/** The violet rift every generator in the game carries, so "source" reads. */
function gloomMaw(a: PixelArt, cx: number, cy: number, r: number): void {
  a.ellipse(cx, cy, r, r * 0.75, '#150c22')
  a.ellipse(cx, cy + r * 0.06, r * 0.68, r * 0.5, '#5b2f92')
  a.ellipse(cx, cy + r * 0.12, r * 0.36, r * 0.26, '#a774e8')
}

/** A heap of coin. `cursed` swings the whole palette red — this is THE tell. */
registerPainter('z5_pile', (a, p) => {
  const s = a.size
  const cx = s / 2
  const cursed = pnum(p, 'cursed', 0) > 0
  const gold = cursed ? '#b8452c' : '#e3b23c'
  const hot = cursed ? '#ff6a4a' : '#ffe9a8'
  const lo = shade(gold, -0.45)

  // the heap: three banks of coin, biggest at the bottom
  a.ellipse(cx, s * 0.78, s * 0.42, s * 0.13, lo)
  a.ellipse(cx, s * 0.72, s * 0.4, s * 0.13, gold)
  a.ellipse(cx - s * 0.14, s * 0.6, s * 0.22, s * 0.1, gold)
  a.ellipse(cx + s * 0.16, s * 0.62, s * 0.2, s * 0.09, shade(gold, -0.15))
  a.ellipse(cx, s * 0.5, s * 0.22, s * 0.1, gold)
  a.ellipse(cx - s * 0.02, s * 0.47, s * 0.13, s * 0.05, hot)
  // a goblet and a crown-shard sticking out of the top, so a pile is loot and
  // not a rock
  a.vline(RD(cx + s * 0.2), RD(s * 0.36), RD(s * 0.14), shade(gold, 0.2))
  a.ellipse(cx + s * 0.2, s * 0.34, s * 0.08, s * 0.05, hot)
  a.rect(RD(cx - s * 0.3), RD(s * 0.5), RD(s * 0.1), 3, hot)
  // the glint
  a.set(RD(cx + s * 0.06), RD(s * 0.44), '#ffffff')
  if (cursed) {
    // a smear of dried red across the coin — visible even without the glow
    a.line(RD(cx - s * 0.24), RD(s * 0.66), RD(cx + s * 0.1), RD(s * 0.54), '#7a1414')
    a.line(RD(cx - s * 0.1), RD(s * 0.74), RD(cx + s * 0.26), RD(s * 0.62), '#7a1414')
  }
})

registerPainter('z5_coffer', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const spent = pnum(p, 'spent', 0) > 0
  const wood = spent ? '#3d2a18' : '#5c3a1c'
  const gilt = spent ? '#7a6330' : '#e3b23c'
  const woodLo = shade(wood, -0.4)
  const stone = shade(pal.wall, -0.2)

  // a strongbox on a low plinth, lid banded in brass
  a.ellipse(cx, s * 0.9, s * 0.42, s * 0.08, shade(stone, -0.3))
  a.rect(RD(cx - s * 0.34), RD(s * 0.44), RD(s * 0.68), RD(s * 0.42), wood)
  a.rect(RD(cx - s * 0.34), RD(s * 0.44), RD(s * 0.68), 4, shade(wood, 0.28))
  a.vline(RD(cx - s * 0.34), RD(s * 0.44), RD(s * 0.42), woodLo)
  a.vline(RD(cx + s * 0.33), RD(s * 0.44), RD(s * 0.42), woodLo)
  // lid, thrown back if it is spent
  if (spent) {
    a.rect(RD(cx - s * 0.34), RD(s * 0.3), RD(s * 0.68), RD(s * 0.1), woodLo)
    a.rect(RD(cx - s * 0.28), RD(s * 0.46), RD(s * 0.56), RD(s * 0.1), '#241a12')
  } else {
    for (let y = 0; y < RD(s * 0.16); y++) {
      const w = RD(s * (0.34 - 0.06 * (y / (s * 0.16))))
      a.hline(RD(cx - w), RD(s * 0.28 + y), w * 2, y % 5 === 4 ? woodLo : wood)
    }
    a.hline(RD(cx - s * 0.34), RD(s * 0.42), RD(s * 0.68), gilt)
  }
  // brass bands and the lock plate
  a.vline(RD(cx - s * 0.16), RD(s * 0.3), RD(s * 0.56), gilt)
  a.vline(RD(cx + s * 0.15), RD(s * 0.3), RD(s * 0.56), gilt)
  a.rect(RD(cx - s * 0.06), RD(s * 0.54), RD(s * 0.12), RD(s * 0.12), gilt)
  a.set(RD(cx), RD(s * 0.6), '#241a12')
  if (!spent) {
    // coin spilling out of the seam: the shop advertises
    a.set(RD(cx - s * 0.24), RD(s * 0.86), '#ffe9a8')
    a.set(RD(cx + s * 0.2), RD(s * 0.88), '#ffe9a8')
  }
})

registerPainter('z5_chest', (a, p) => {
  const s = a.size
  const cx = s / 2
  const open = pnum(p, 'open', 0) > 0
  const wood = pstr(p, 'wood', '#5c3a1c')
  const gilt = pstr(p, 'gilt', '#e3b23c')
  const woodLo = shade(wood, -0.42)
  const woodHi = shade(wood, 0.24)

  a.ellipse(cx, s * 0.88, s * 0.38, s * 0.08, '#1a0d08')
  // body
  a.rect(RD(cx - s * 0.32), RD(s * 0.48), RD(s * 0.64), RD(s * 0.36), wood)
  a.vline(RD(cx - s * 0.32), RD(s * 0.48), RD(s * 0.36), woodLo)
  a.vline(RD(cx + s * 0.31), RD(s * 0.48), RD(s * 0.36), woodLo)
  a.hline(RD(cx - s * 0.32), RD(s * 0.82), RD(s * 0.64), woodLo)
  if (open) {
    // lid thrown back, and nothing left inside
    a.rect(RD(cx - s * 0.32), RD(s * 0.3), RD(s * 0.64), RD(s * 0.09), woodLo)
    a.rect(RD(cx - s * 0.26), RD(s * 0.5), RD(s * 0.52), RD(s * 0.12), '#150c08')
    a.hline(RD(cx - s * 0.32), RD(s * 0.3), RD(s * 0.64), gilt)
  } else {
    // domed lid
    for (let y = 0; y < RD(s * 0.2); y++) {
      const k = y / (s * 0.2)
      const w = RD(s * (0.2 + 0.12 * k))
      a.hline(RD(cx - w), RD(s * 0.29 + y), w * 2, y % 6 === 5 ? woodLo : wood)
    }
    a.hline(RD(cx - s * 0.26), RD(s * 0.3), RD(s * 0.52), woodHi)
    a.hline(RD(cx - s * 0.32), RD(s * 0.47), RD(s * 0.64), gilt)
  }
  // brass straps + lock
  a.vline(RD(cx - s * 0.14), RD(s * 0.3), RD(s * 0.54), gilt)
  a.vline(RD(cx + s * 0.13), RD(s * 0.3), RD(s * 0.54), gilt)
  a.rect(RD(cx - s * 0.05), RD(s * 0.52), RD(s * 0.1), RD(s * 0.1), gilt)
  a.set(RD(cx), RD(s * 0.57), '#241a12')
})

/** The Mimic, mid-appetite. `gape` opens the lid and shows what is inside. */
registerPainter('z5_mimic', (a, p) => {
  const s = a.size
  const cx = s / 2
  const wood = pstr(p, 'wood', '#5c3a1c')
  const gilt = pstr(p, 'gilt', '#e3b23c')
  const maw = pstr(p, 'maw', '#8a1f1f')
  const woodLo = shade(wood, -0.42)

  a.ellipse(cx, s * 0.9, s * 0.36, s * 0.07, '#1a0d08')
  // legs: it grew some
  for (let i = -1; i <= 1; i += 2) {
    a.line(RD(cx + i * s * 0.22), RD(s * 0.78), RD(cx + i * s * 0.34), RD(s * 0.9), woodLo)
    a.line(RD(cx + i * s * 0.1), RD(s * 0.8), RD(cx + i * s * 0.16), RD(s * 0.92), woodLo)
  }
  // body
  a.rect(RD(cx - s * 0.3), RD(s * 0.5), RD(s * 0.6), RD(s * 0.3), wood)
  a.hline(RD(cx - s * 0.3), RD(s * 0.78), RD(s * 0.6), woodLo)
  // the maw, wide open
  a.rect(RD(cx - s * 0.26), RD(s * 0.4), RD(s * 0.52), RD(s * 0.16), '#170a08')
  a.rect(RD(cx - s * 0.24), RD(s * 0.44), RD(s * 0.48), RD(s * 0.1), maw)
  // teeth, top and bottom
  for (let i = 0; i < 6; i++) {
    const x = RD(cx - s * 0.24 + i * s * 0.096)
    a.vline(x, RD(s * 0.4), 3, '#f2e6c8')
    a.vline(x + 2, RD(s * 0.52), 3, '#f2e6c8')
  }
  // lid, hinged back over the top
  a.rect(RD(cx - s * 0.3), RD(s * 0.22), RD(s * 0.6), RD(s * 0.16), wood)
  a.hline(RD(cx - s * 0.3), RD(s * 0.22), RD(s * 0.6), shade(wood, 0.26))
  a.hline(RD(cx - s * 0.3), RD(s * 0.38), RD(s * 0.6), gilt)
  a.vline(RD(cx - s * 0.12), RD(s * 0.22), RD(s * 0.16), gilt)
  a.vline(RD(cx + s * 0.11), RD(s * 0.22), RD(s * 0.16), gilt)
  // two coin-eyes on the lid
  a.disc(cx - s * 0.14, s * 0.3, s * 0.05, gilt)
  a.disc(cx + s * 0.13, s * 0.3, s * 0.05, gilt)
  a.set(RD(cx - s * 0.14), RD(s * 0.3), '#8a1f1f')
  a.set(RD(cx + s * 0.13), RD(s * 0.3), '#8a1f1f')
})

registerPainter('z5_plate', (a, p) => {
  const s = a.size
  const cx = s / 2
  const steel = pstr(p, 'steel', '#6d6a63')
  const gilt = pstr(p, 'gilt', '#e3b23c')
  const dark = pstr(p, 'dark', '#241a12')
  const steelLo = shade(steel, -0.4)
  const steelHi = shade(steel, 0.3)

  // wide, low and squared off — nothing else in the zone has this footprint
  a.ellipse(cx, s * 0.9, s * 0.36, s * 0.07, '#1a0d08')
  // greaves
  a.rect(RD(cx - s * 0.16), RD(s * 0.7), RD(s * 0.11), RD(s * 0.2), steelLo)
  a.rect(RD(cx + s * 0.05), RD(s * 0.7), RD(s * 0.11), RD(s * 0.2), steelLo)
  // cuirass: broad, banded, empty
  a.rect(RD(cx - s * 0.24), RD(s * 0.38), RD(s * 0.48), RD(s * 0.34), steel)
  a.hline(RD(cx - s * 0.24), RD(s * 0.38), RD(s * 0.48), steelHi)
  a.hline(RD(cx - s * 0.24), RD(s * 0.5), RD(s * 0.48), gilt)
  a.hline(RD(cx - s * 0.24), RD(s * 0.62), RD(s * 0.48), steelLo)
  // the hollow: a dark slot where a face would be
  a.ellipse(cx, s * 0.26, s * 0.15, s * 0.14, steel)
  a.hline(RD(cx - s * 0.12), RD(s * 0.26), RD(s * 0.24), dark)
  a.set(RD(cx - s * 0.06), RD(s * 0.26), gilt)
  a.set(RD(cx + s * 0.04), RD(s * 0.26), gilt)
  a.hline(RD(cx - s * 0.16), RD(s * 0.16), RD(s * 0.32), gilt) // crest band
  // pauldrons
  a.ellipse(cx - s * 0.3, s * 0.4, s * 0.11, s * 0.09, steelHi)
  a.ellipse(cx + s * 0.3, s * 0.4, s * 0.11, s * 0.09, steelHi)
  // THE TOWER SHIELD, held out on the right: the biggest shape in the sprite,
  // so which way it is facing is never a question
  a.rect(RD(cx + s * 0.24), RD(s * 0.24), RD(s * 0.2), RD(s * 0.56), gilt)
  a.rect(RD(cx + s * 0.27), RD(s * 0.28), RD(s * 0.14), RD(s * 0.48), shade(gilt, -0.35))
  a.vline(RD(cx + s * 0.33), RD(s * 0.3), RD(s * 0.44), shade(gilt, 0.4))
  a.set(RD(cx + s * 0.33), RD(s * 0.5), '#ffffff')
})

registerPainter('z5_coinling', (a, p) => {
  const s = a.size
  const cx = s / 2
  const gold = pstr(p, 'gold', '#e3b23c')
  const hot = pstr(p, 'hot', '#ffe9a8')
  const eye = pstr(p, 'eye', '#ff4a4a')
  const lo = shade(gold, -0.45)

  // a stack of coin with legs — low, round, and unmistakably money
  for (let i = 0; i < 4; i++) {
    const y = s * (0.74 - i * 0.11)
    const w = s * (0.34 - i * 0.03)
    a.ellipse(cx + (i % 2 === 0 ? -1 : 1), y, w, s * 0.09, i % 2 === 0 ? gold : shade(gold, -0.12))
    a.ellipse(cx + (i % 2 === 0 ? -1 : 1), y - s * 0.02, w * 0.6, s * 0.045, hot)
  }
  // scuttling legs
  for (let i = -1; i <= 1; i += 2) {
    a.line(RD(cx + i * s * 0.2), RD(s * 0.78), RD(cx + i * s * 0.34), RD(s * 0.9), lo)
    a.line(RD(cx + i * s * 0.1), RD(s * 0.8), RD(cx + i * s * 0.18), RD(s * 0.92), lo)
  }
  a.set(RD(cx - s * 0.08), RD(s * 0.36), eye)
  a.set(RD(cx + s * 0.06), RD(s * 0.36), eye)
})

registerPainter('z5_warden', (a, p) => {
  const s = a.size
  const cx = s / 2
  const robe = pstr(p, 'robe', '#5a3a52')
  const gilt = pstr(p, 'gilt', '#e3b23c')
  const bone = pstr(p, 'bone', '#e8e0c8')
  const robeLo = shade(robe, -0.4)
  const robeHi = shade(robe, 0.25)

  // hovering, hooded, with a disc already spinning at its hand
  for (let i = -2; i <= 2; i++) {
    a.vline(RD(cx + i * s * 0.08), RD(s * 0.78), RD(s * (0.08 + 0.04 * ((i + 3) % 3))), robeLo)
  }
  for (let y = 0; y < RD(s * 0.4); y++) {
    const w = RD(s * (0.09 + 0.2 * (y / (s * 0.4))))
    a.hline(RD(cx - w), RD(s * 0.4 + y), w * 2, y % 7 === 6 ? robeLo : robe)
  }
  a.ellipse(cx, s * 0.36, s * 0.2, s * 0.08, robeHi)
  // hood + reliquary mask
  a.ellipse(cx, s * 0.24, s * 0.16, s * 0.16, robe)
  a.ellipse(cx, s * 0.26, s * 0.1, s * 0.11, '#150c14')
  a.ellipse(cx, s * 0.26, s * 0.07, s * 0.08, gilt)
  a.set(RD(cx - s * 0.03), RD(s * 0.26), '#8a1f1f')
  a.set(RD(cx + s * 0.02), RD(s * 0.26), '#8a1f1f')
  a.hline(RD(cx - s * 0.16), RD(s * 0.14), RD(s * 0.32), gilt)
  // the disc, held out and edge-on
  a.ring(cx + s * 0.3, s * 0.5, s * 0.12, gilt)
  a.ring(cx + s * 0.3, s * 0.5, s * 0.07, bone)
  a.set(RD(cx + s * 0.3), RD(s * 0.5), '#ffffff')
})

/** The gilded Courtier: zone 3's body in gold leaf. Same silhouette on purpose. */
registerPainter('z5_courtier', (a, p) => {
  const s = a.size
  const cx = s / 2
  const cloth = pstr(p, 'cloth', '#4a2f1a')
  const gilt = pstr(p, 'gilt', '#e3b23c')
  const glint = pstr(p, 'glint', '#ff4a4a')
  const clothLo = shade(cloth, -0.4)
  const clothHi = shade(cloth, 0.28)
  const giltLo = shade(gilt, -0.3)

  a.ellipse(cx - s * 0.22, s * 0.56, s * 0.16, s * 0.26, clothLo)
  a.rect(RD(cx - s * 0.1), RD(s * 0.7), 3, RD(s * 0.2), giltLo)
  a.rect(RD(cx + s * 0.04), RD(s * 0.7), 3, RD(s * 0.2), giltLo)
  a.hline(RD(cx - s * 0.14), RD(s * 0.9), 5, clothLo)
  a.hline(RD(cx + s * 0.02), RD(s * 0.9), 5, clothLo)
  a.rect(RD(cx - s * 0.16), RD(s * 0.5), RD(s * 0.32), RD(s * 0.24), cloth)
  a.vline(RD(cx), RD(s * 0.52), RD(s * 0.2), clothLo)
  a.hline(RD(cx - s * 0.16), RD(s * 0.5), RD(s * 0.32), clothHi)
  // gilded cuirass where the court wore bone
  a.ellipse(cx, s * 0.45, s * 0.19, s * 0.13, gilt)
  a.ellipse(cx, s * 0.48, s * 0.11, s * 0.07, giltLo)
  // the ruff, now a collar of coin
  a.ellipse(cx, s * 0.33, s * 0.27, s * 0.07, gilt)
  a.ellipse(cx, s * 0.315, s * 0.2, s * 0.04, shade(gilt, 0.25))
  a.ellipse(cx, s * 0.2, s * 0.12, s * 0.13, '#e8e0c8')
  a.ellipse(cx, s * 0.12, s * 0.09, s * 0.05, gilt)
  a.rect(RD(cx - s * 0.09), RD(s * 0.19), RD(s * 0.18), 2, '#191524')
  a.set(RD(cx - s * 0.07), RD(s * 0.19), glint)
  a.set(RD(cx + s * 0.05), RD(s * 0.19), glint)
  const wx = RD(cx + s * 0.34)
  a.vline(wx, RD(s * 0.26), RD(s * 0.44), gilt)
  a.vline(wx + 1, RD(s * 0.3), RD(s * 0.36), giltLo)
  a.set(wx, RD(s * 0.25), glint)
  a.hline(wx - 2, RD(s * 0.7), 5, clothHi)
})

registerPainter('z5_gen_heap', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const gold = pstr(p, 'gold', '#e3b23c')
  const lo = shade(gold, -0.45)
  const hot = shade(gold, 0.4)
  const stone = shade(pal.wall, -0.25)

  // a hill of coin with something moving under it
  a.ellipse(cx, s * 0.86, s * 0.46, s * 0.1, shade(stone, -0.3))
  for (let i = 0; i < 4; i++) {
    const y = s * (0.8 - i * 0.1)
    const w = s * (0.44 - i * 0.07)
    a.ellipse(cx, y, w, s * 0.09, i % 2 === 0 ? gold : lo)
  }
  a.ellipse(cx, s * 0.44, s * 0.2, s * 0.07, hot)
  // spilled coin at the base
  for (let i = 0; i < 7; i++) {
    a.set(RD(s * 0.12 + i * s * 0.11), RD(s * 0.9), i % 2 === 0 ? gold : hot)
  }
  gloomMaw(a, cx, s * 0.66, s * 0.14)
})

registerPainter('z5_gen_rack', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const steel = pstr(p, 'steel', '#6d6a63')
  const gilt = pstr(p, 'gilt', '#e3b23c')
  const stone = shade(pal.wall, -0.22)
  const steelLo = shade(steel, -0.38)

  // a rack of empty armour, waiting to be filled
  a.ellipse(cx, s * 0.9, s * 0.44, s * 0.08, shade(stone, -0.32))
  a.rect(RD(cx - s * 0.4), RD(s * 0.76), RD(s * 0.8), RD(s * 0.12), stone)
  a.hline(RD(cx - s * 0.4), RD(s * 0.76), RD(s * 0.8), shade(stone, 0.3))
  // the crossbar
  a.hline(RD(cx - s * 0.38), RD(s * 0.22), RD(s * 0.76), gilt)
  for (let i = -1; i <= 1; i++) {
    const x = cx + i * s * 0.26
    a.vline(RD(x), RD(s * 0.22), RD(s * 0.12), steelLo)
    // hanging cuirasses
    a.rect(RD(x - s * 0.1), RD(s * 0.34), RD(s * 0.2), RD(s * 0.22), steel)
    a.hline(RD(x - s * 0.1), RD(s * 0.34), RD(s * 0.2), shade(steel, 0.3))
    a.hline(RD(x - s * 0.1), RD(s * 0.44), RD(s * 0.2), gilt)
    a.ellipse(x, s * 0.3, s * 0.07, s * 0.06, steelLo)
  }
  gloomMaw(a, cx, s * 0.64, s * 0.13)
})

registerPainter('z5_gen_alcove', (a, p, pal) => {
  const s = a.size
  const cx = s / 2
  const gilt = pstr(p, 'gilt', '#e3b23c')
  const bone = pstr(p, 'bone', '#e8e0c8')
  const stone = shade(pal.wall, -0.2)
  const giltLo = shade(gilt, -0.35)

  // a niche of stacked discs behind a gilded arch
  a.ellipse(cx, s * 0.9, s * 0.4, s * 0.08, shade(stone, -0.3))
  a.rect(RD(cx - s * 0.32), RD(s * 0.3), RD(s * 0.64), RD(s * 0.56), stone)
  for (let y = 0; y < RD(s * 0.2); y++) {
    const w = RD(s * (0.12 + 0.2 * (y / (s * 0.2))))
    a.hline(RD(cx - w), RD(s * 0.12 + y), w * 2, y % 5 === 4 ? giltLo : gilt)
  }
  a.rect(RD(cx - s * 0.24), RD(s * 0.34), RD(s * 0.48), RD(s * 0.44), '#1c1208')
  // the discs on their pegs
  for (let i = 0; i < 3; i++) {
    const y = s * (0.44 + i * 0.14)
    a.ring(cx - s * 0.1, y, s * 0.08, gilt)
    a.ring(cx + s * 0.11, y, s * 0.08, i % 2 === 0 ? bone : gilt)
  }
  a.vline(RD(cx - s * 0.32), RD(s * 0.3), RD(s * 0.56), giltLo)
  a.vline(RD(cx + s * 0.31), RD(s * 0.3), RD(s * 0.56), giltLo)
  gloomMaw(a, cx, s * 0.62, s * 0.12)
})

/** The relic disc, drawn edge-on so its spin reads while it flies. */
registerPainter('z5_disc', (a, p) => {
  const s = a.size
  const cx = s / 2
  const color = pstr(p, 'color', '#ffe9a8')
  a.ring(cx, cx, s * 0.44, shade(color, -0.35))
  a.ring(cx, cx, s * 0.34, color)
  a.ellipse(cx, cx, s * 0.16, s * 0.16, shade(color, 0.4))
  a.set(RD(cx), RD(cx), '#ffffff')
})

// ---------------------------------------------------------------- registry
// registerZoneContent bakes recipes for enemies and generators; a projectile's
// sprite and every fixture recipe has to be registered by hand.

registerRecipe('z5_pile_clean', { key: 'z5_pile', size: 34, params: { cursed: 0 } })
registerRecipe('z5_pile_cursed', { key: 'z5_pile', size: 34, params: { cursed: 1 } })
registerRecipe('z5_coffer', { key: 'z5_coffer', size: 36, params: { spent: 0 } })
registerRecipe('z5_coffer_spent', { key: 'z5_coffer', size: 36, params: { spent: 1 } })
registerRecipe('z5_chest', { key: 'z5_chest', size: 32, params: { open: 0 } })
registerRecipe('z5_chest_open', { key: 'z5_chest', size: 32, params: { open: 1 } })
for (const p of ZONE5_PROJECTILES) {
  registerRecipe(p.id, { key: 'z5_disc', size: p.id === 'relicdisc' ? 18 : 16, params: { color: p.color } })
}

registerZoneContent({
  enemies: ZONE5_ENEMIES as EnemyDef[],
  projectiles: ZONE5_PROJECTILES,
  generators: ZONE5_GENERATORS,
})
registerZone(ZONE5, zone5Mechanic)
